import { DesktopGridItem, ShortcutGroup } from 'shared/types/desktop-grid'

import {
  checkSlotFits,
  createGrid,
  markSlotOccupied
} from '../gridLayoutHelpers'
import { getShortcutGroupCollisionItems } from '../shortcutGroupBounds'
import { AutoArrangeHandler } from './types'

/**
 * Arranges only ungrouped shortcuts. Group members (and pinned shortcuts) are
 * fixed in place and their full group boundaries are reserved as obstacles.
 * The supplied arranger is still used to determine the placement order, so
 * name/date/type sorting behaves exactly as it does without groups.
 */
export const arrangeItemsWithShortcutGroups = (
  arranger: AutoArrangeHandler,
  items: DesktopGridItem[],
  cols: number,
  rows: number,
  groups: ShortcutGroup[]
): DesktopGridItem[] => {
  if (groups.length === 0) return arranger(items, cols, rows)

  const groupedIds = new Set(groups.flatMap(group => group.memberIds))
  const movableItems = items.filter(
    item => !item.isPinned && !groupedIds.has(item.id)
  )
  if (movableItems.length === 0 || cols <= 0 || rows <= 0) return items

  // Running the existing arranger without obstacles gives us its exact sort
  // order. We then apply that order to a placement pass that reserves the
  // fixed shortcuts and group boundaries.
  const arrangedWithoutObstacles = arranger(movableItems, cols, rows)
  const originalOrder = new Map(
    movableItems.map((item, index) => [item.id, index])
  )
  const placementOrder = [...arrangedWithoutObstacles].sort(
    (first, second) =>
      first.position.row - second.position.row ||
      first.position.col - second.position.col ||
      (originalOrder.get(first.id) ?? 0) - (originalOrder.get(second.id) ?? 0)
  )

  const grid = createGrid(rows, cols)
  const reserve = (item: {
    position: { row: number; col: number }
    size: { width: number; height: number }
  }) => {
    const startRow = Math.max(0, item.position.row)
    const endRow = Math.min(rows, item.position.row + item.size.height)
    const startCol = Math.max(0, item.position.col)
    const endCol = Math.min(cols, item.position.col + item.size.width)
    for (let row = startRow; row < endRow; row += 1) {
      for (let col = startCol; col < endCol; col += 1) {
        grid[row][col] = true
      }
    }
  }

  items
    .filter(item => item.isPinned || groupedIds.has(item.id))
    .forEach(reserve)
  getShortcutGroupCollisionItems(groups, items).forEach(reserve)

  const arrangedPositions = new Map<string, DesktopGridItem['position']>()
  placementOrder.forEach(item => {
    let position: DesktopGridItem['position'] | undefined
    for (let row = 0; row < rows && !position; row += 1) {
      for (let col = 0; col < cols && !position; col += 1) {
        if (
          !checkSlotFits(
            grid,
            row,
            col,
            item.size.width,
            item.size.height,
            rows,
            cols
          )
        ) {
          continue
        }
        position = { row, col }
      }
    }

    if (!position) position = item.position
    arrangedPositions.set(item.id, position)
    if (
      position.row >= 0 &&
      position.col >= 0 &&
      checkSlotFits(
        grid,
        position.row,
        position.col,
        item.size.width,
        item.size.height,
        rows,
        cols
      )
    ) {
      markSlotOccupied(
        grid,
        position.row,
        position.col,
        item.size.width,
        item.size.height
      )
    }
  })

  return items.map(item => {
    const position = arrangedPositions.get(item.id)
    return position ? { ...item, position } : item
  })
}
